// El equipo tal como lo ve quien mira, calculado en UN sitio: su bandeja, los bloques de la lista (con «Con Santi», las
// subidas en la fila de su agente y los aparcados), el orden plano y quién le necesita. Lo leen la lista, el riel
// plegado, «Saltar a…» y Alt+↑/↓ del chat: antes cada uno lo calculaba a su manera y el teclado se saltaba filas (04-11,
// auditoría de código B1/B2).
//
// El chat abierto conserva su sitio: al abrirlo el servidor lo marca leído y, con «lo no leído primero», su fila
// saltaría bajo el cursor. El almacén guarda cómo estaba al abrirlo (`noLeidoAlAbrir`) y todos ordenan con eso.
//
// Aparcar depende del reloj (24 h sin hablar, 7 días aparcado): en vez de sondear, UN temporizador al próximo cambio.
import { useEffect, useMemo, useState } from 'react';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { useSubidas } from '../despliegues/store';
import { useSantiPorAgente } from '../pelotas/porAgente';
import { useOficinaStore } from '../store';
import { agruparPorSituacion, proximoCambioDe, type SantiPorAgente } from './situacion';
import { useBandeja } from './useBandeja';

/** setTimeout no aguanta más de ~24,8 días. */
const MAXIMO_TEMPORIZADOR_MS = 2 ** 31 - 1;

export function useEquipo(contratados: Record<string, ContratadoDTO>) {
  const santi = useSantiPorAgente();
  const { porAgente: subidas } = useSubidas();
  const bandeja = useBandeja(contratados, subidas);
  const congelado = useOficinaStore((s) => s.noLeidoAlAbrir);
  const { visibles } = bandeja;
  const ahora = useRelojAparcar(visibles, santi, subidas);
  const grupos = useMemo(
    () => agruparPorSituacion(visibles, (c) => (congelado && c.agentId === congelado.agentId ? congelado.noLeido : c.noLeido), santi, { subidas, ahora }),
    [visibles, congelado, santi, subidas, ahora],
  );
  const orden = useMemo(() => grupos.flatMap((g) => g.miembros), [grupos]);
  // «Te necesita» = está en «Te toca» (situacion.ts `necesitaAPau`), con el mismo contexto que la lista.
  const necesita = useMemo(() => {
    const ids = new Set(grupos.find((g) => g.bloque === 'te_toca')?.miembros.map((c) => c.agentId));
    return (c: Pick<ContratadoDTO, 'agentId'>) => ids.has(c.agentId);
  }, [grupos]);
  return { ...bandeja, santi, subidas, grupos, orden, necesita, ahora };
}

/** La hora con la que se mira el equipo: se adelanta sola justo cuando alguien se aparca o pasa a «proponer cerrar». */
function useRelojAparcar(visibles: readonly ContratadoDTO[], santi: SantiPorAgente, subidas: ReadonlyMap<string, { creadoEn: string }>): number {
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    const t0 = Date.now();
    let proximo = Number.POSITIVE_INFINITY;
    for (const c of visibles) {
      const p = proximoCambioDe(c, santi.get(c.agentId), { subida: subidas.get(c.agentId) ?? null, ahora: t0 });
      if (p !== null && p < proximo) proximo = p;
    }
    if (!Number.isFinite(proximo)) return;
    // Un segundo de margen: al despertar, el cambio ya ha pasado.
    const id = setTimeout(() => setAhora(Date.now()), Math.min(Math.max(0, proximo - t0) + 1000, MAXIMO_TEMPORIZADOR_MS));
    return () => clearTimeout(id);
  }, [visibles, santi, subidas, ahora]);
  return ahora;
}

/**
 * Alt+↓ / Alt+↑: el siguiente (o anterior) que te necesita en el orden de la lista, sin contar el abierto y dando la
 * vuelta. Con `todos`, el siguiente sin más (Alt+Mayús). null = no queda nadie (Nada más te toca).
 */
export function siguienteEnEquipo<C extends Pick<ContratadoDTO, 'agentId'>>(orden: readonly C[], desde: string | null, paso: 1 | -1, cuenta: (c: C) => boolean): C | null {
  const n = orden.length;
  if (!n) return null;
  const i = orden.findIndex((c) => c.agentId === desde);
  for (let k = 1; k <= n; k += 1) {
    const c = orden[(((i < 0 ? (paso === 1 ? -1 : 0) : i) + paso * k) % n + n) % n]!;
    if (c.agentId !== desde && cuenta(c)) return c;
  }
  return null;
}
